import React from 'react';

// Portal Phase C — clean-rendered task descriptions (display fix #1).
// ---------------------------------------------------------------------------
// Imported Notion descriptions leak raw export syntax: `## headers`, `- [ ]`
// checklists, URL-encoded supporting-file link dumps, bare attachment refs. We
// parse a SAFE markdown subset into React elements (no dangerouslySetInnerHTML,
// no new dependency) so the text reads clean in the detail view + previews.
//
// Scope is deliberately small — the shapes that actually appear in the migrated
// data — and anything unrecognized falls through as plain text, never raw markup.

// Notion file exports drop lines like:
//   ![image.png](attachment%2Fabc%2Fimage.png)
//   [Some%20File.pdf](https://prod-files.notion-static.com/...)
//   Supporting files: ...
// and bare "file.ext" reference dumps. Strip those lines from the body — the
// detail view surfaces real attachments in its own "Supporting files" section.
const FILE_DUMP_RE =
	/(notion-static\.com|prod-files|attachment%2|\.(png|jpe?g|gif|pdf|docx?|xlsx?|csv|mov|mp4|heic)\b)/i;

function isFileDumpLine(line) {
	const t = line.trim();
	if (!t) return false;
	if (/^!\[/.test(t)) return true; // markdown image embed
	if (/^supporting files?\s*:/i.test(t)) return true;
	// A line that is essentially just a link/ref to an exported file.
	if (FILE_DUMP_RE.test(t) && /\]\(|https?:\/\/|%2F/.test(t)) return true;
	return false;
}

// A fenced code block: ``` or ~~~, with an optional language tag.
const FENCE_RE = /^\s*(```+|~~~+)\s*([A-Za-z0-9_+-]*)\s*$/;

// Split raw markdown into block tokens.
//
// Exported for tests. It is the honest surface to assert block structure on:
// `toPlainText` runs a cleanup pass that strips stray backticks, so it cannot
// see a fence bug even when one is on screen, and `MarkdownText` returns React.
export function tokenize(src) {
	const lines = String(src || '').replace(/\r\n?/g, '\n').split('\n');
	const blocks = [];
	let para = [];
	let list = null; // { ordered, checklist, items: [] }
	let fence = null; // { marker, lang, lines: [] } while inside a fenced block

	const flushPara = () => {
		if (para.length) {
			blocks.push({ type: 'p', text: para.join(' ').trim() });
			para = [];
		}
	};
	const flushList = () => {
		if (list && list.items.length) blocks.push(list);
		list = null;
	};
	const flushFence = () => {
		if (fence) blocks.push({ type: 'pre', lang: fence.lang, text: fence.lines.join('\n') });
		fence = null;
	};

	for (const raw of lines) {
		const line = raw.replace(/\s+$/, '');

		// Fenced code is handled BEFORE every other rule, and deliberately
		// before the blank-line and file-dump checks: inside a fence, a blank
		// line is content, `- x` is not a list, `# x` is not a heading, and a
		// line mentioning a .png is not a Notion file dump. Without this the
		// paragraph branch caught fence lines and `para.join(' ')` welded the
		// whole block into one run-on line with the backticks still showing —
		// which is what task descriptions holding CSS or shell snippets have
		// been rendering as.
		const fenceEdge = line.match(FENCE_RE);
		if (fence) {
			// Only a matching marker type closes the block, so ``` inside a ~~~
			// block stays content.
			if (fenceEdge && fenceEdge[1][0] === fence.marker[0]) flushFence();
			else fence.lines.push(raw.replace(/\s+$/, ''));
			continue;
		}
		if (fenceEdge) {
			flushPara();
			flushList();
			fence = { marker: fenceEdge[1], lang: fenceEdge[2] || '', lines: [] };
			continue;
		}

		if (!line.trim()) {
			flushPara();
			flushList();
			continue;
		}
		if (isFileDumpLine(line)) continue;

		const heading = line.match(/^(#{1,6})\s+(.*)$/);
		if (heading) {
			flushPara();
			flushList();
			const level = Math.min(heading[1].length, 6);
			blocks.push({ type: 'h', level, text: heading[2].trim() });
			continue;
		}

		const check = line.match(/^\s*[-*]\s+\[([ xX])\]\s+(.*)$/);
		if (check) {
			flushPara();
			if (!list || !list.checklist) {
				flushList();
				list = { type: 'list', checklist: true, ordered: false, items: [] };
			}
			list.items.push({
				checked: check[1].toLowerCase() === 'x',
				text: check[2].trim(),
			});
			continue;
		}

		const bullet = line.match(/^\s*[-*]\s+(.*)$/);
		if (bullet) {
			flushPara();
			if (!list || list.checklist || list.ordered) {
				flushList();
				list = { type: 'list', checklist: false, ordered: false, items: [] };
			}
			list.items.push({ text: bullet[1].trim() });
			continue;
		}

		const ordered = line.match(/^\s*\d+[.)]\s+(.*)$/);
		if (ordered) {
			flushPara();
			if (!list || list.checklist || !list.ordered) {
				flushList();
				list = { type: 'list', checklist: false, ordered: true, items: [] };
			}
			list.items.push({ text: ordered[1].trim() });
			continue;
		}

		flushList();
		para.push(line.trim());
	}
	// An UNTERMINATED fence still emits its block. Dropping it would silently
	// swallow everything after the opening marker, which is worse than showing
	// a code block the author forgot to close.
	flushFence();
	flushPara();
	flushList();
	return blocks;
}

// Inline parse: **bold**, *italic*/_italic_, `code`, [text](url). Links are
// rendered only for http(s)/mailto; anything else stays plain text.
//
// `decorate(str, key)` is an optional hook applied to the PLAIN-TEXT runs left
// over after markdown parsing — it's how comments layer @mention chips on top
// without this module having to know what a mention is. Kept as a callback so
// cleanMarkdown stays dependency-free (no import of the people canon, no cycle
// back into taskModel), which is the same reason it hand-rolls markdown instead
// of pulling in a library.
function renderInline(text, keyPrefix, decorate) {
	const out = [];
	let rest = String(text || '');
	let i = 0;
	const push = (s, k) => {
		if (!s) return;
		out.push(decorate ? decorate(s, k) : s);
	};
	const pattern =
		/(\*\*([^*]+)\*\*)|(\*([^*]+)\*)|(_([^_]+)_)|(`([^`]+)`)|(\[([^\]]+)\]\((https?:\/\/[^\s)]+|mailto:[^\s)]+)\))/;
	while (rest.length) {
		const m = rest.match(pattern);
		if (!m) {
			push(rest, `${keyPrefix}-t${i++}`);
			break;
		}
		if (m.index > 0) push(rest.slice(0, m.index), `${keyPrefix}-t${i++}`);
		const k = `${keyPrefix}-${i++}`;
		if (m[1]) out.push(<strong key={k}>{m[2]}</strong>);
		else if (m[3]) out.push(<em key={k}>{m[4]}</em>);
		else if (m[5]) out.push(<em key={k}>{m[6]}</em>);
		else if (m[7]) out.push(<code key={k} className="wt-md__code">{m[8]}</code>);
		else if (m[9])
			out.push(
				<a key={k} href={m[11]} target="_blank" rel="noopener noreferrer">
					{m[10]}
				</a>
			);
		rest = rest.slice(m.index + m[0].length);
	}
	return out;
}

// Render cleaned markdown as React. `plain` mode returns a single de-marked
// string (for row previews / clamps) instead of block elements.
export function MarkdownText({ source, className = '', clampLines, decorate }) {
	const blocks = tokenize(source);
	if (!blocks.length) return null;
	const style = clampLines
		? {
				display: '-webkit-box',
				WebkitLineClamp: clampLines,
				WebkitBoxOrient: 'vertical',
				overflow: 'hidden',
		  }
		: undefined;
	return (
		<div className={`wt-md ${className}`} style={style}>
			{blocks.map((b, bi) => {
				if (b.type === 'h') {
					return (
						<div key={bi} className={`wt-md__h wt-md__h--${b.level}`}>
							{renderInline(b.text, `h${bi}`, decorate)}
						</div>
					);
				}
				if (b.type === 'pre') {
					// Rendered as literal text — no renderInline. Inside a code
					// block `**x**` is two asterisks and a URL is not a link;
					// parsing it would corrupt the very thing being quoted.
					return (
						<pre key={bi} className="wt-md__pre" data-lang={b.lang || undefined}>
							<code>{b.text}</code>
						</pre>
					);
				}
				if (b.type === 'list') {
					if (b.checklist) {
						return (
							<ul key={bi} className="wt-md__checklist">
								{b.items.map((it, ii) => (
									<li
										key={ii}
										className={
											it.checked ? 'is-checked' : undefined
										}>
										<i
											className={`fa-sharp-duotone fa-solid ${
												it.checked
													? 'fa-square-check'
													: 'fa-square'
											}`}
										/>
										<span>{renderInline(it.text, `c${bi}-${ii}`, decorate)}</span>
									</li>
								))}
							</ul>
						);
					}
					const ListTag = b.ordered ? 'ol' : 'ul';
					return (
						<ListTag key={bi} className="wt-md__list">
							{b.items.map((it, ii) => (
								<li key={ii}>{renderInline(it.text, `l${bi}-${ii}`, decorate)}</li>
							))}
						</ListTag>
					);
				}
				return (
					<p key={bi} className="wt-md__p">
						{renderInline(b.text, `p${bi}`, decorate)}
					</p>
				);
			})}
		</div>
	);
}

// Flatten cleaned markdown to a single plain-text line (for compact previews).
export function toPlainText(source) {
	const blocks = tokenize(source);
	const parts = [];
	for (const b of blocks) {
		// Code contributes its text to a preview line, but newlines collapse
		// with everything else via the \s+ squeeze below.
		if (b.type === 'h' || b.type === 'p' || b.type === 'pre') parts.push(b.text);
		else if (b.type === 'list')
			parts.push(b.items.map((it) => it.text).join(' · '));
	}
	return parts
		.join(' ')
		.replace(/\*\*|__|[*_`]/g, '')
		.replace(/\[([^\]]+)\]\([^)]*\)/g, '$1')
		.replace(/\s+/g, ' ')
		.trim();
}
